import { get, getNum, getStr, getTable, isDateMDY, isEmail, isNo, isPhone, isYes } from '@shared';

// ---------- General Rules (All States) -------------------------

// R1: Representative Agent Information - percentages must sum to 100%
const ruleRepresentativeAgentPercentSum: Rule = {
  id: 'representative.agent.percent.sum',
  check: (form): RuleResult => {
    const rows = getTable(form, 'representativeAgentInfo');
    if (!rows.length) return { ok: true };

    let sum = 0;
    let badCell = false;

    for (const r of rows) {
      const pct = getNum({ pct: r['percentage'] }, 'pct');
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        badCell = true;
        break;
      }
      sum += pct;
    }

    if (badCell) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'REPRESENTATIVE_PERCENT_INVALID',
        message: 'Each representative percentage must be a number between 0 and 100.',
        refs: ['representativeAgentInfo'],
        category: 'formatting',
      };
    }

    if (sum > 0 && Math.round(sum) !== 100) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'REPRESENTATIVE_SUM_100',
        message: `Representative percentages must total 100% (currently ${sum}%).`,
        refs: ['representativeAgentInfo'],
        category: 'data-consistency',
      };
    }

    return { ok: true };
  },
};

// R2: Representative contact information format validation
const ruleRepresentativeContactFormat: Rule = {
  id: 'representative.contact.format',
  check: (form): RuleResult => {
    const rows = getTable(form, 'representativeAgentInfo');
    const errs: string[] = [];
    const refs: string[] = ['representativeAgentInfo'];

    rows.forEach((r, i) => {
      const idx = i + 1;
      const email = String(r['email'] ?? '').trim();
      if (email && !isEmail(email)) {
        errs.push(`Row ${idx}: Email address format looks invalid.`);
      }
    });

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'REPRESENTATIVE_CONTACT_FORMAT',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// R3: Replacement policy coherence - if discontinuing or using funds, must provide details
const ruleReplacementPolicyCoherence: Rule = {
  id: 'replacement.policy.coherence',
  when: form =>
    isYes(get(form, 'replacementDiscontinuingPolicy')) || isYes(get(form, 'replacementUsingFunds')),
  check: (form): RuleResult => {
    const rows = getTable(form, 'replacementExistingPolicies');

    if (!rows.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'REPLACEMENT_DETAILS_REQUIRED',
        message:
          'Existing policy details are required when discontinuing a policy or using funds from existing policies.',
        refs: [
          'replacementDiscontinuingPolicy',
          'replacementUsingFunds',
          'replacementExistingPolicies',
        ],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R4: Replacement reason required if discontinuing policy
const ruleReplacementReasonRequired: Rule = {
  id: 'replacement.reason.required',
  when: form => isYes(get(form, 'replacementDiscontinuingPolicy')),
  check: (form): RuleResult => {
    const reason = getStr(form, 'replacementReason');

    if (!reason) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'REPLACEMENT_REASON_REQUIRED',
        message: 'Replacement reason must be provided when discontinuing an existing policy.',
        refs: ['replacementReason', 'replacementDiscontinuingPolicy'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R5: Coverage existing insurance details coherence
const ruleCoverageExistingInsuranceCoherence: Rule = {
  id: 'coverage.existing.insurance.coherence',
  when: form =>
    isYes(get(form, 'coverageHasOtherLtc')) ||
    isYes(get(form, 'coverageHadLtcLastYear')) ||
    isYes(get(form, 'coverageHasExistingInsurance')),
  check: (form): RuleResult => {
    const rows = getTable(form, 'coverageExistingInsuranceDetails');

    if (!rows.length) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'COVERAGE_DETAILS_EXPECTED',
        message:
          'Existing insurance details are expected when other LTC or insurance coverage is indicated.',
        refs: [
          'coverageHasOtherLtc',
          'coverageHadLtcLastYear',
          'coverageHasExistingInsurance',
          'coverageExistingInsuranceDetails',
        ],
        category: 'general',
      };
    }

    return { ok: true };
  },
};

// R6: Pending application details required when indicated
const ruleCoveragePendingApplicationDetails: Rule = {
  id: 'coverage.pending.application.details',
  when: form => isYes(get(form, 'coverageApplicationPending')),
  check: (form): RuleResult => {
    const company = getStr(form, 'coveragePendingCompany');
    const amount = getStr(form, 'coveragePendingAmount');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!company) {
      errs.push('Pending application company name is required.');
      refs.push('coveragePendingCompany');
    }

    if (!amount) {
      errs.push('Pending application amount is required.');
      refs.push('coveragePendingAmount');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PENDING_APPLICATION_DETAILS',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'coverageApplicationPending'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R7: Life settlement details required when indicated
const ruleCoverageLifeSettlementDetails: Rule = {
  id: 'coverage.life.settlement.details',
  when: form => isYes(get(form, 'coverageSoldLifeSettlement')),
  check: (form): RuleResult => {
    const company = getStr(form, 'coverageSettlementCompany');
    const amount = getStr(form, 'coverageSettlementAmount');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!company) {
      errs.push('Life settlement company name is required.');
      refs.push('coverageSettlementCompany');
    }

    if (!amount) {
      errs.push('Life settlement amount is required.');
      refs.push('coverageSettlementAmount');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'LIFE_SETTLEMENT_DETAILS',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'coverageSoldLifeSettlement'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R8: LTC had last year details required
const ruleCoverageLtcLastYearDetails: Rule = {
  id: 'coverage.ltc.last.year.details',
  when: form => isYes(get(form, 'coverageHadLtcLastYear')),
  check: (form): RuleResult => {
    const company = getStr(form, 'coverageLtcCompanyName');
    const lapseDate = getStr(form, 'coverageLtcLapseDate');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!company) {
      errs.push('LTC company name is required when LTC coverage was held in the last year.');
      refs.push('coverageLtcCompanyName');
    }

    if (!lapseDate) {
      errs.push('LTC lapse date is required when LTC coverage was held in the last year.');
      refs.push('coverageLtcLapseDate');
    } else if (!isDateMDY(lapseDate)) {
      errs.push('LTC lapse date must be in mm/dd/yyyy format.');
      refs.push('coverageLtcLapseDate');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'LTC_LAST_YEAR_DETAILS',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'coverageHadLtcLastYear'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R9: LTC worksheet - other resource specify required when "other" selected
const ruleLtcOtherResourceSpecify: Rule = {
  id: 'ltc.other.resource.specify',
  when: form => {
    const resources = get(form, 'ltcResourcesForPremium');
    if (Array.isArray(resources)) {
      return resources.some(r => String(r).toLowerCase() === 'other');
    }
    return String(resources ?? '')
      .toLowerCase()
      .includes('other');
  },
  check: (form): RuleResult => {
    const specify = getStr(form, 'ltcOtherResourceSpecify');

    if (!specify) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'LTC_OTHER_RESOURCE_REQUIRED',
        message: 'Please specify other resource when "Other" is selected.',
        refs: ['ltcResourcesForPremium', 'ltcOtherResourceSpecify'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R10: LTC worksheet - future costs other specify required
const ruleLtcPayForFutureCostsOtherSpecify: Rule = {
  id: 'ltc.pay.future.costs.other.specify',
  when: form => {
    const ltcBuyInflation = get(form, 'ltcBuyInflationProtection');
    if (!isNo(ltcBuyInflation)) return false;

    const payCosts = get(form, 'ltcPayForFutureCosts');
    if (Array.isArray(payCosts)) {
      return payCosts.some(c => String(c).toLowerCase() === 'other');
    }
    return String(payCosts ?? '')
      .toLowerCase()
      .includes('other');
  },
  check: (form): RuleResult => {
    const specify = getStr(form, 'ltcPayForFutureCostsOther');

    if (!specify) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'LTC_FUTURE_COSTS_OTHER_REQUIRED',
        message: 'Please specify other method when "Other" is selected for future costs.',
        refs: ['ltcPayForFutureCosts', 'ltcPayForFutureCostsOther'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R11: AIDS/HIV health condition eligibility risk
const ruleMedicalAidsEligibilityRisk: Rule = {
  id: 'medical.aids.eligibility.risk',
  check: (form): RuleResult => {
    const aidsFirst = get(form, 'medicalAidsFirstInsured');
    const aidsSecond = get(form, 'medicalAidsSecondInsured');
    const issues: string[] = [];
    const refs: string[] = [];

    if (isYes(aidsFirst)) {
      issues.push(
        'First insured answered "Yes" to AIDS/ARC/immune disorder; this is typically an automatic decline per OneAmerica guidelines.',
      );
      refs.push('medicalAidsFirstInsured');
    }

    if (isYes(aidsSecond)) {
      issues.push(
        'Second insured answered "Yes" to AIDS/ARC/immune disorder; this is typically an automatic decline per OneAmerica guidelines.',
      );
      refs.push('medicalAidsSecondInsured');
    }

    if (issues.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'AIDS_ELIGIBILITY_RISK',
        message: issues.join(' '),
        refs,
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R12: Pending diagnostic tests eligibility risk
const ruleMedicalDiagnosticTestsRisk: Rule = {
  id: 'medical.diagnostic.tests.risk',
  check: (form): RuleResult => {
    const testsFirst = get(form, 'medicalDiagnosticTestsFirstInsured');
    const testsSecond = get(form, 'medicalDiagnosticTestsSecondInsured');
    const issues: string[] = [];
    const refs: string[] = [];

    if (isYes(testsFirst)) {
      issues.push(
        'First insured has pending diagnostic tests, hospitalization, or surgery; application may be delayed until completion.',
      );
      refs.push('medicalDiagnosticTestsFirstInsured');
    }

    if (isYes(testsSecond)) {
      issues.push(
        'Second insured has pending diagnostic tests, hospitalization, or surgery; application may be delayed until completion.',
      );
      refs.push('medicalDiagnosticTestsSecondInsured');
    }

    if (issues.length) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'PENDING_TESTS_ADVISORY',
        message: issues.join(' '),
        refs,
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// R13: Representative replacement knowledge consistency
const ruleRepresentativeReplacementConsistency: Rule = {
  id: 'representative.replacement.consistency',
  check: (form): RuleResult => {
    const replacementKnowledge = get(form, 'representativeReplacementKnowledge');
    const replacingPolicy = get(form, 'coverageReplacingPolicy');
    const hasExisting = get(form, 'replacementExistingInsurance');

    const errs: string[] = [];
    const refs: string[] = [];

    if (isYes(replacementKnowledge) && !isYes(replacingPolicy) && !isYes(hasExisting)) {
      errs.push(
        'Representative indicates knowledge of replacement but coverage questions do not reflect this.',
      );
      refs.push('representativeReplacementKnowledge', 'coverageReplacingPolicy');
    }

    if (!isYes(replacementKnowledge) && (isYes(replacingPolicy) || isYes(hasExisting))) {
      errs.push(
        'Coverage indicates replacement activity but representative has not acknowledged replacement knowledge.',
      );
      refs.push('representativeReplacementKnowledge', 'coverageReplacingPolicy');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'REPLACEMENT_CONSISTENCY',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'general',
      };
    }

    return { ok: true };
  },
};

// R14: Certificate of Delivery - no response details required when statements not true
const ruleCertDeliveryNoResponseDetails: Rule = {
  id: 'cert.delivery.no.response.details',
  when: form => isNo(get(form, 'certDeliveryStatementsTrue')),
  check: (form): RuleResult => {
    const details = getStr(form, 'certDeliveryNoResponseDetails');

    if (!details) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CERT_DELIVERY_DETAILS_REQUIRED',
        message: 'Please provide details when certificate of delivery statements are not true.',
        refs: ['certDeliveryStatementsTrue', 'certDeliveryNoResponseDetails'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R15: Representative evaluation with others - details required
const ruleRepresentativeEvaluationDetails: Rule = {
  id: 'representative.evaluation.details',
  when: form => isYes(get(form, 'representativeEvaluateWithOthers')),
  check: (form): RuleResult => {
    const name = getStr(form, 'representativeOtherApplicantName');
    const relationship = getStr(form, 'representativeOtherApplicantRelationship');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!name) {
      errs.push('Other applicant name is required.');
      refs.push('representativeOtherApplicantName');
    }

    if (!relationship) {
      errs.push('Other applicant relationship is required.');
      refs.push('representativeOtherApplicantRelationship');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'EVALUATION_DETAILS_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'representativeEvaluateWithOthers'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R16: Representative contact information format
const ruleRepresentativeContactInfoFormat: Rule = {
  id: 'representative.contact.info.format',
  check: (form): RuleResult => {
    const email = getStr(form, 'representativeContactEmail');
    const phone = getStr(form, 'representativeContactPhone');
    const errs: string[] = [];
    const refs: string[] = [];

    if (email && !isEmail(email)) {
      errs.push('Representative contact email format looks invalid.');
      refs.push('representativeContactEmail');
    }

    if (phone && !isPhone(phone)) {
      errs.push('Representative contact phone format looks invalid.');
      refs.push('representativeContactPhone');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'REPRESENTATIVE_CONTACT_FORMAT',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// R17: Medicaid eligibility flag
const ruleCoverageMedicaidEligibilityAdvisory: Rule = {
  id: 'coverage.medicaid.eligibility.advisory',
  check: (form): RuleResult => {
    const eligible = get(form, 'coverageEligibleMedicaid');

    if (isYes(eligible)) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'MEDICAID_ELIGIBILITY',
        message:
          'Applicant indicates potential Medicaid eligibility; underwriting may require additional review.',
        refs: ['coverageEligibleMedicaid'],
        category: 'carrier-requirements',
      };
    }

    return { ok: true };
  },
};

// ---------- STATE-SPECIFIC RULES (CA) ------------------------------------------

// R18: CA - Lapse notice recipient phone format when designated
const ruleCALapseNoticeRecipientPhone: Rule = {
  id: 'ca.lapse.notice.recipient.phone',
  states: ['CA'],
  when: form => getStr(form, 'lapseNoticeDesignation') === 'designate',
  check: (form): RuleResult => {
    const phone = getStr(form, 'lapseNoticeRecipientPhoneNumber');

    if (phone && !isPhone(phone)) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CA_LAPSE_NOTICE_PHONE_FORMAT',
        message: 'Lapse notice recipient phone number format looks invalid.',
        refs: ['lapseNoticeRecipientPhoneNumber'],
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// R19: CA - Accelerated benefits replace LTC consistency
const ruleCAAcceleratedBenefitsConsistency: Rule = {
  id: 'ca.accelerated.benefits.consistency',
  states: ['CA'],
  check: (form): RuleResult => {
    const replaceLtc = get(form, 'acceleratedBenefitsReplaceLtc');
    const hasOtherLtc = get(form, 'coverageHasOtherLtc');
    const hadLtcLastYear = get(form, 'coverageHadLtcLastYear');

    if (isYes(replaceLtc) && !isYes(hasOtherLtc) && !isYes(hadLtcLastYear)) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'CA_ACCELERATED_BENEFITS_CONSISTENCY',
        message:
          'Applicant intends to replace LTC insurance but does not indicate having other LTC coverage.',
        refs: ['acceleratedBenefitsReplaceLtc', 'coverageHasOtherLtc', 'coverageHadLtcLastYear'],
        category: 'general',
      };
    }

    return { ok: true };
  },
};

// R20: CA - Replacement notice table required when policy replacement indicated
const ruleCAReplacementNoticeTableRequired: Rule = {
  id: 'ca.replacement.notice.table.required',
  states: ['CA'],
  when: form => isYes(get(form, 'policyReplacement')),
  check: (form): RuleResult => {
    const rows = getTable(form, 'replacementNoticeTable');

    if (!rows.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CA_REPLACEMENT_NOTICE_TABLE_REQUIRED',
        message: 'Replacement notice table is required when policy replacement is indicated.',
        refs: ['replacementNoticeTable', 'policyReplacement'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R21: CA - SABP benefits specify required when selected
const ruleCA_SABPBenefitsSpecify: Rule = {
  id: 'ca.sabp.benefits.specify',
  states: ['CA'],
  when: form => {
    const benefits = get(form, 'sabpBenefits');
    if (Array.isArray(benefits)) {
      return (
        benefits.some(b => String(b).toLowerCase().includes('additional')) ||
        benefits.some(b => String(b).toLowerCase().includes('other'))
      );
    }
    return false;
  },
  check: (form): RuleResult => {
    const benefits = get(form, 'sabpBenefits');
    const errs: string[] = [];
    const refs: string[] = ['sabpBenefits'];

    if (Array.isArray(benefits)) {
      if (benefits.some(b => String(b).toLowerCase().includes('additional'))) {
        const specify = getStr(form, 'sabpAdditionalBenefitsSpecify');
        if (!specify) {
          errs.push('Please specify additional benefits details.');
          refs.push('sabpAdditionalBenefitsSpecify');
        }
      }

      if (benefits.some(b => String(b).toLowerCase().includes('other'))) {
        const specify = getStr(form, 'sabpOtherSpecify');
        if (!specify) {
          errs.push('Please specify other benefits details.');
          refs.push('sabpOtherSpecify');
        }
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CA_SABP_SPECIFY_REQUIRED',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R22: CA - Page 13 purpose of visit specify required
const ruleCAPage13PurposeSpecify: Rule = {
  id: 'ca.page13.purpose.specify',
  states: ['CA'],
  when: form => {
    const purposes = get(form, 'page13PurposeOfVisit');
    if (Array.isArray(purposes)) {
      return (
        purposes.some(p => String(p).toLowerCase().includes('other-insurance')) ||
        purposes.some(p => String(p).toLowerCase().includes('other-financial'))
      );
    }
    return false;
  },
  check: (form): RuleResult => {
    const purposes = get(form, 'page13PurposeOfVisit');
    const errs: string[] = [];
    const refs: string[] = ['page13PurposeOfVisit'];

    if (Array.isArray(purposes)) {
      if (purposes.some(p => String(p).toLowerCase().includes('other-insurance'))) {
        const specify = getStr(form, 'page13OtherInsuranceProductsSpecify');
        if (!specify) {
          errs.push('Please specify other insurance products.');
          refs.push('page13OtherInsuranceProductsSpecify');
        }
      }

      if (purposes.some(p => String(p).toLowerCase().includes('other-financial'))) {
        const specify = getStr(form, 'page13OtherFinancialProductsSpecify');
        if (!specify) {
          errs.push('Please specify other financial products.');
          refs.push('page13OtherFinancialProductsSpecify');
        }
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CA_PAGE13_PURPOSE_SPECIFY',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R23: CA - Page 13 contact information format
const ruleCAPage13ContactFormat: Rule = {
  id: 'ca.page13.contact.format',
  states: ['CA'],
  check: (form): RuleResult => {
    const phone = getStr(form, 'page13TelephoneNumber');
    const errs: string[] = [];
    const refs: string[] = [];

    if (phone && !isPhone(phone)) {
      errs.push('Page 13 telephone number format looks invalid.');
      refs.push('page13TelephoneNumber');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CA_PAGE13_CONTACT_FORMAT',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// R24: CA - ADC spouse details required when has spouse indicated
const ruleCAAdcSpouseDetailsRequired: Rule = {
  id: 'ca.adc.spouse.details.required',
  states: ['CA'],
  when: form => isYes(get(form, 'adcHasSpouse')),
  check: (form): RuleResult => {
    const name = getStr(form, 'adcSpouseName');
    const email = getStr(form, 'adcSpouseEmail');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!name) {
      errs.push('Spouse name is required.');
      refs.push('adcSpouseName');
    }

    if (!email) {
      errs.push('Spouse email is required.');
      refs.push('adcSpouseEmail');
    } else if (!isEmail(email)) {
      errs.push('Spouse email format looks invalid.');
      refs.push('adcSpouseEmail');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CA_ADC_SPOUSE_DETAILS',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'adcHasSpouse'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R25: CA - ADC legal representative details required when indicated
const ruleCAAdcLegalRepDetailsRequired: Rule = {
  id: 'ca.adc.legal.rep.details.required',
  states: ['CA'],
  when: form => isYes(get(form, 'adcHasLegalRepresentative')),
  check: (form): RuleResult => {
    const name = getStr(form, 'adcLegalRepresentativeName');
    const email = getStr(form, 'adcLegalRepresentativeEmail');
    const errs: string[] = [];
    const refs: string[] = [];

    if (!name) {
      errs.push('Legal representative name is required.');
      refs.push('adcLegalRepresentativeName');
    }

    if (!email) {
      errs.push('Legal representative email is required.');
      refs.push('adcLegalRepresentativeEmail');
    } else if (!isEmail(email)) {
      errs.push('Legal representative email format looks invalid.');
      refs.push('adcLegalRepresentativeEmail');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CA_ADC_LEGAL_REP_DETAILS',
        message: errs.join(' '),
        refs: Array.from(new Set([...refs, 'adcHasLegalRepresentative'])),
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R26: CA - Agent email format validation
const ruleCAAgentEmailFormat: Rule = {
  id: 'ca.agent.email.format',
  states: ['CA'],
  check: (form): RuleResult => {
    const agentEmail = getStr(form, 'agentEmailAddress');
    const residentEmail = getStr(form, 'residentAgentEmailAddress');
    const errs: string[] = [];
    const refs: string[] = [];

    if (agentEmail && !isEmail(agentEmail)) {
      errs.push('Agent email address format looks invalid.');
      refs.push('agentEmailAddress');
    }

    if (residentEmail && !isEmail(residentEmail)) {
      errs.push('Resident agent email address format looks invalid.');
      refs.push('residentAgentEmailAddress');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CA_AGENT_EMAIL_FORMAT',
        message: errs.join(' '),
        refs,
        category: 'formatting',
      };
    }

    return { ok: true };
  },
};

// ---------- STATE-SPECIFIC RULES (FL) ------------------------------------------

// R27: FL - Representative agent info additional column (Florida license ID)
const ruleFLRepresentativeAgentLicenseRequired: Rule = {
  id: 'fl.representative.agent.license.required',
  states: ['FL'],
  check: (form): RuleResult => {
    const rows = getTable(form, 'representativeAgentInfo');
    const errs: string[] = [];
    const refs: string[] = ['representativeAgentInfo'];

    rows.forEach((r, i) => {
      const idx = i + 1;
      const licenseId = String(r['id-number'] ?? '').trim();
      if (!licenseId) {
        errs.push(`Row ${idx}: Florida License Identification Number is required.`);
      }
    });

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'FL_REPRESENTATIVE_LICENSE_REQUIRED',
        message: errs.join(' '),
        refs,
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// R28: FL - Replacement notice table required when policy replacement indicated
const ruleFLReplacementNoticeTableRequired: Rule = {
  id: 'fl.replacement.notice.table.required',
  states: ['FL'],
  when: form => isYes(get(form, 'policyReplacement')),
  check: (form): RuleResult => {
    const rows = getTable(form, 'replacementNoticeTable');

    if (!rows.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'FL_REPLACEMENT_NOTICE_TABLE_REQUIRED',
        message: 'Replacement notice table is required when policy replacement is indicated.',
        refs: ['replacementNoticeTable', 'policyReplacement'],
        category: 'missing-information',
      };
    }

    return { ok: true };
  },
};

// ---------- EXPORT ------------------------------------------------------------

export const oneAmericaCAStateRules: Rule[] = [
  ruleCALapseNoticeRecipientPhone,
  ruleCAAcceleratedBenefitsConsistency,
  ruleCAReplacementNoticeTableRequired,
  ruleCA_SABPBenefitsSpecify,
  ruleCAPage13PurposeSpecify,
  ruleCAPage13ContactFormat,
  ruleCAAdcSpouseDetailsRequired,
  ruleCAAdcLegalRepDetailsRequired,
  ruleCAAgentEmailFormat,
];

export const oneAmericaFLStateRules: Rule[] = [
  ruleFLRepresentativeAgentLicenseRequired,
  ruleFLReplacementNoticeTableRequired,
];

export const oneAmericaAllStatesRules: Rule[] = [
  ruleRepresentativeAgentPercentSum,
  ruleRepresentativeContactFormat,
  ruleReplacementPolicyCoherence,
  ruleReplacementReasonRequired,
  ruleCoverageExistingInsuranceCoherence,
  ruleCoveragePendingApplicationDetails,
  ruleCoverageLifeSettlementDetails,
  ruleCoverageLtcLastYearDetails,
  ruleLtcOtherResourceSpecify,
  ruleLtcPayForFutureCostsOtherSpecify,
  ruleMedicalAidsEligibilityRisk,
  ruleMedicalDiagnosticTestsRisk,
  ruleRepresentativeReplacementConsistency,
  ruleCertDeliveryNoResponseDetails,
  ruleRepresentativeEvaluationDetails,
  ruleRepresentativeContactInfoFormat,
  ruleCoverageMedicaidEligibilityAdvisory,
  ...oneAmericaCAStateRules,
  ...oneAmericaFLStateRules,
];

export function evaluateOANigo(
  form: PolicyFormSubmissionData,
  state?: string,
): RuleResult[] {
  return oneAmericaAllStatesRules
    .filter(r => !r.states || !state || r.states.includes(state))
    .filter(r => (r.when ? r.when(form) : true))
    .map(r => {
      try {
        return r.check(form);
      } catch (e) {
        return {
          ok: false,
          severity: 'high-confidence' as const,
          code: 'RULE_RUNTIME',
          message: `Rule ${r.id} failed: ${String(e)}`,
          category: 'general' as const,
        };
      }
    })
    .filter(res => !res.ok);
}
